import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  LinearProgress,
  Pagination,
  Button,
  useTheme,
  Tooltip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  AdminPanelSettings as AdminIcon,
  FolderShared as FolderIcon
} from '@mui/icons-material';
import { dashboardService } from '../../../services/dashboard-service';
import { toast } from 'react-toastify';

// High-fidelity fallback search results matching backend DTO
const mockAccountSearchResult = {
  totalCount: 5,
  accounts: [
    { id: "acc_premium_01", ownerId: "usr_9281a", role: "SUBSCRIBER", maxProfilesAllowed: 100, currentProfileCount: 45, subscriptionStatus: "ACTIVE", subscriptionType: "PAID", subscriptionStartDate: "2026-01-10T00:00:00Z", subscriptionEndDate: "2027-01-10T00:00:00Z", referralCode: "SCHOOL_PROMO_2026", createdAt: "2026-01-10T08:30:00Z", createdBy: "usr_system", updatedAt: "2026-05-20T12:00:00Z", updatedBy: "usr_9281a" },
    { id: "acc_trial_02", ownerId: "usr_3847b", role: "SUBSCRIBER", maxProfilesAllowed: 5, currentProfileCount: 2, subscriptionStatus: "ACTIVE", subscriptionType: "TRIAL", subscriptionStartDate: "2026-05-15T00:00:00Z", subscriptionEndDate: "2026-06-15T00:00:00Z", referralCode: null, createdAt: "2026-05-15T14:45:00Z", createdBy: "usr_system", updatedAt: null, updatedBy: null },
    { id: "acc_unlimited_03", ownerId: "usr_platform_admin", role: "PLATFORM_OWNER", maxProfilesAllowed: 99999, currentProfileCount: 15, subscriptionStatus: "ACTIVE", subscriptionType: "UNLIMITED", subscriptionStartDate: "2025-01-01T00:00:00Z", subscriptionEndDate: "2099-12-31T23:59:59Z", referralCode: "INTERNAL_DEV", createdAt: "2025-01-01T00:00:00Z", createdBy: "usr_root", updatedAt: "2026-01-01T00:00:00Z", updatedBy: "usr_platform_admin" },
    { id: "acc_premium_04", ownerId: "usr_5561f", role: "SUBSCRIBER", maxProfilesAllowed: 50, currentProfileCount: 48, subscriptionStatus: "IN_GRACE_PERIOD", subscriptionType: "PAID", subscriptionStartDate: "2025-05-20T00:00:00Z", subscriptionEndDate: "2026-05-20T00:00:00Z", referralCode: "EARLY_ADOPTER", createdAt: "2025-05-20T10:00:00Z", createdBy: "usr_system", updatedAt: "2026-05-20T10:00:00Z", updatedBy: "usr_system" },
    { id: "acc_school_05", ownerId: "usr_7726e", role: "SUBSCRIBER", maxProfilesAllowed: 200, currentProfileCount: 0, subscriptionStatus: "EXPIRED", subscriptionType: "PAID", subscriptionStartDate: "2025-04-15T00:00:00Z", subscriptionEndDate: "2026-04-15T00:00:00Z", referralCode: "REGIONAL_GRANT", createdAt: "2025-04-15T09:12:00Z", createdBy: "usr_system", updatedAt: "2026-04-15T00:00:00Z", updatedBy: "usr_system" }
  ]
};

const TenantManagementTab = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isMocked, setIsMocked] = useState(false);

  // Search parameters state
  const [term, setTerm] = useState('');
  const [type, setType] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [page, setPage] = useState(1);
  const size = 5; // Page size

  // Selected account for drawer view
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const query = {
        term: term.trim() ? term.trim() : null,
        subscriptionTypes: type === 'ALL' ? [] : [type],
        subscriptionStatuses: status === 'ALL' ? [] : [status],
        pageNumber: page - 1,
        pageSize: size
      };

      const result = await dashboardService.searchAccounts(query);
      setAccounts(result.items || []);
      setTotalCount(result.totalItems || 0);
      setIsMocked(false);
    } catch (err) {
      console.warn("Using offline mock data fallback for account search query:", err.message);
      setIsMocked(true);
      // Filter mock data locally to maintain a high-fidelity simulation experience
      let filtered = [...mockAccountSearchResult.accounts];
      
      if (term.trim()) {
        const queryTerm = term.toLowerCase().trim();
        filtered = filtered.filter(a => 
          a.id.toLowerCase().includes(queryTerm) ||
          a.ownerId.toLowerCase().includes(queryTerm) ||
          (a.referralCode && a.referralCode.toLowerCase().includes(queryTerm))
        );
      }
      
      if (type !== 'ALL') {
        filtered = filtered.filter(a => a.subscriptionType === type);
      }
      
      if (status !== 'ALL') {
        filtered = filtered.filter(a => a.subscriptionStatus === status);
      }

      const total = filtered.length;
      const start = (page - 1) * size;
      const paged = filtered.slice(start, start + size);
      
      setAccounts(paged);
      setTotalCount(total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, status]);

  useEffect(() => {
    if (selectedAccount) {
      const fetchOwnerDetails = async () => {
        try {
          setOwnerLoading(true);
          setOwnerDetails(null);
          // Fetch from searchUsers using selectedAccount.ownerId
          const query = {
            term: selectedAccount.ownerId,
            role: null,
            status: null,
            page: 0,
            size: 1
          };
          const result = await dashboardService.searchUsers(query);
          if (result.users && result.users.length > 0) {
            const matchedUser = result.users.find(u => u.userId === selectedAccount.ownerId) || result.users[0];
            setOwnerDetails({
              name: matchedUser.name,
              email: matchedUser.email,
              isMocked: false
            });
          } else {
            // Fallback mock details based on typical mock users list
            if (selectedAccount.ownerId === "usr_9281a") {
              setOwnerDetails({ name: "John Doe", email: "johndoe@school.edu", isMocked: true });
            } else if (selectedAccount.ownerId === "usr_3847b") {
              setOwnerDetails({ name: "Alice Cooper", email: "reader_alice@school.edu", isMocked: true });
            } else if (selectedAccount.ownerId === "usr_5561f") {
              setOwnerDetails({ name: "Eve Online", email: "reader_eve@school.edu", isMocked: true });
            } else if (selectedAccount.ownerId === "usr_7726e") {
              setOwnerDetails({ name: "Charlie Chaplin", email: "charlie@school.edu", isMocked: true });
            } else {
              setOwnerDetails({
                name: "Real Account Owner",
                email: "owner@school.edu",
                isMocked: true
              });
            }
          }
        } catch (err) {
          console.warn("Could not load real owner details:", err.message);
          // Fallback mock details
          if (selectedAccount.ownerId === "usr_9281a") {
            setOwnerDetails({ name: "John Doe", email: "johndoe@school.edu", isMocked: true });
          } else if (selectedAccount.ownerId === "usr_3847b") {
            setOwnerDetails({ name: "Alice Cooper", email: "reader_alice@school.edu", isMocked: true });
          } else if (selectedAccount.ownerId === "usr_5561f") {
            setOwnerDetails({ name: "Eve Online", email: "reader_eve@school.edu", isMocked: true });
          } else if (selectedAccount.ownerId === "usr_7726e") {
            setOwnerDetails({ name: "Charlie Chaplin", email: "charlie@school.edu", isMocked: true });
          } else {
            setOwnerDetails({
              name: "Real Account Owner",
              email: "owner@school.edu",
              isMocked: true
            });
          }
        } finally {
          setOwnerLoading(false);
        }
      };
      fetchOwnerDetails();
    } else {
      setOwnerDetails(null);
    }
  }, [selectedAccount]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAccounts();
  };

  const copyToClipboard = (text, typeName = "ID") => {
    navigator.clipboard.writeText(text);
    toast.info(`Copied ${typeName} to clipboard: ${text}`);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    // Check for extreme placeholder end dates
    if (isoString.startsWith("2099")) return "Forever (Unlimited)";
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Color chips maps matching M3 design
  const getRoleChipColor = (role) => {
    switch (role) {
      case 'PLATFORM_OWNER':
        return {
          backgroundColor: theme.palette.tertiaryContainer || '#f3e5f5',
          color: theme.palette.onTertiaryContainer || '#4a148c'
        };
      case 'SUBSCRIBER':
        return {
          backgroundColor: theme.palette.secondaryContainer || '#e8f0fe',
          color: theme.palette.onSecondaryContainer || '#1a73e8'
        };
      default:
        return {};
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: theme.palette.success.main,
          borderColor: 'rgba(76, 175, 80, 0.3)'
        };
      case 'IN_GRACE_PERIOD':
        return {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: theme.palette.warning.main,
          borderColor: 'rgba(255, 152, 0, 0.3)'
        };
      case 'EXPIRED':
      case 'CANCELLED':
      case 'SUSPENDED':
        return {
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
          color: theme.palette.error.main,
          borderColor: 'rgba(244, 67, 54, 0.3)'
        };
      default:
        return {
          backgroundColor: theme.palette.surfaceContainerLow,
          color: theme.palette.text.secondary,
          borderColor: theme.palette.outlineVariant
        };
    }
  };

  const getTypeChipColor = (type) => {
    switch (type) {
      case 'PAID':
        return {
          backgroundColor: theme.palette.primaryContainer || '#e6f4ea',
          color: theme.palette.onPrimaryContainer || '#137333'
        };
      case 'TRIAL':
        return {
          backgroundColor: 'rgba(63, 81, 181, 0.12)',
          color: '#3f51b5',
          borderColor: 'rgba(63, 81, 181, 0.25)'
        };
      case 'UNLIMITED':
        return {
          backgroundColor: 'rgba(156, 39, 176, 0.12)',
          color: '#9c27b0',
          borderColor: 'rgba(156, 39, 176, 0.25)'
        };
      default:
        return {};
    }
  };

  return (
    <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
      
      {isMocked && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: '12px',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          border: `1px solid ${theme.palette.error.main}`,
          color: theme.palette.error.main,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          ⚠️ Demo Mode: Backend API offline. Displaying explicitly mocked tenant registry records.
        </Box>
      )}

      {/* Search Filter Header Card */}
      <Card sx={{ mb: 4, borderRadius: '16px' }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={3} alignItems="center">
              
              <Grid item xs={12} sm={5} md={6}>
                <TextField
                  fullWidth
                  label="Search accounts"
                  placeholder="Search by Account ID, Owner User ID, or referral code..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="type-select-label">Subscription Type</InputLabel>
                  <Select
                    labelId="type-select-label"
                    value={type}
                    label="Subscription Type"
                    onChange={(e) => { setType(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Types</MenuItem>
                    <MenuItem value="PAID">Paid</MenuItem>
                    <MenuItem value="TRIAL">Trial</MenuItem>
                    <MenuItem value="UNLIMITED">Unlimited</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={status}
                    label="Status"
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="IN_GRACE_PERIOD">In Grace Period</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    <MenuItem value="EXPIRED">Expired</MenuItem>
                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={1} md={2}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<FilterIcon />}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  Filter
                </Button>
              </Grid>

            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Results Datatable Card */}
      <Card sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.surfaceContainerHigh }}>
                <TableCell sx={{ fontWeight: 700 }}>ACCOUNT ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>OWNER USER ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ROLE</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>TYPE</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>QUOTA UTILIZATION</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>EXPIRATION DATE</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                      🔍 No tenant accounts match your current filter parameters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => {
                  const utilizationPercentage = Math.min(100, Math.round((account.currentProfileCount / account.maxProfilesAllowed) * 100));
                  return (
                    <TableRow
                      key={account.id}
                      hover
                      onClick={() => setSelectedAccount(account)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.surfaceContainerLow
                        },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      {/* Account ID */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Click to copy Account ID">
                          <Chip
                            label={account.id}
                            size="small"
                            onClick={() => copyToClipboard(account.id, "Account ID")}
                            onDelete={() => copyToClipboard(account.id, "Account ID")}
                            deleteIcon={<CopyIcon style={{ fontSize: '14px' }} />}
                            sx={{ 
                              fontFamily: 'monospace', 
                              cursor: 'pointer',
                              borderRadius: '6px'
                            }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Owner User ID */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Click to copy Owner ID">
                          <Chip
                            label={account.ownerId}
                            size="small"
                            onClick={() => copyToClipboard(account.ownerId, "Owner ID")}
                            onDelete={() => copyToClipboard(account.ownerId, "Owner ID")}
                            deleteIcon={<CopyIcon style={{ fontSize: '14px' }} />}
                            sx={{ 
                              fontFamily: 'monospace', 
                              cursor: 'pointer',
                              borderRadius: '6px'
                            }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Role */}
                      <TableCell>
                        <Chip
                          label={account.role}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            borderRadius: '8px',
                            ...getRoleChipColor(account.role)
                          }}
                        />
                      </TableCell>

                      {/* Type */}
                      <TableCell>
                        <Chip
                          label={account.subscriptionType}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            borderRadius: '8px',
                            ...getTypeChipColor(account.subscriptionType)
                          }}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={account.subscriptionStatus.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            borderRadius: '8px',
                            ...getStatusChipColor(account.subscriptionStatus)
                          }}
                        />
                      </TableCell>

                      {/* Quota Utilization Progress */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '120px' }}>
                          <Box sx={{ width: '100%' }}>
                            <LinearProgress
                              variant="determinate"
                              value={utilizationPercentage}
                              sx={{
                                height: 6,
                                borderRadius: '3px',
                                backgroundColor: theme.palette.surfaceContainerHighest,
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: utilizationPercentage > 90 ? theme.palette.error.main : theme.palette.primary.main,
                                  borderRadius: '3px'
                                }
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {account.currentProfileCount} / {account.maxProfilesAllowed >= 9999 ? "∞" : account.maxProfilesAllowed}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Expiration Date */}
                      <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
                        {formatDate(account.subscriptionEndDate)}
                      </TableCell>

                      {/* Action Menu (Drawer Trigger) */}
                      <TableCell align="center">
                        <IconButton 
                          size="small"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>

                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalCount > size && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Pagination
              count={Math.ceil(totalCount / size)}
              page={page}
              onChange={(e, p) => setPage(p)}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>
        )}
      </Card>

      {/* Account Details sliding sidebar drawer */}
      <Drawer
        anchor="right"
        open={!!selectedAccount}
        onClose={() => setSelectedAccount(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 0,
            borderRadius: { xs: 0, sm: '24px 0 0 24px' },
            boxSizing: 'border-box'
          }
        }}
      >
        {selectedAccount && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            
            {/* Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 3, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.surfaceContainerHigh
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FolderIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Account Registry Details {isMocked && <span style={{ color: theme.palette.error.main, fontSize: '14px' }}>(Mocked)</span>}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedAccount(null)} size="medium">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content Body */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3, '& > *': { flexShrink: 0 } }}>
              
              {/* Account Profile Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Identity Parameters
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText 
                        primary="ACCOUNT ID" 
                        secondary={selectedAccount.id} 
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                      <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                        onClick={() => copyToClipboard(selectedAccount.id, "Account ID")}
                        sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                      >
                        Copy
                      </Button>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText 
                        primary="OWNER USER ID" 
                        secondary={selectedAccount.ownerId} 
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                      />
                      <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                        onClick={() => copyToClipboard(selectedAccount.ownerId, "Owner ID")}
                        sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                      >
                        Copy
                      </Button>
                    </ListItem>

                    {ownerLoading ? (
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={20} />
                      </ListItem>
                    ) : ownerDetails ? (
                      <>
                        <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <ListItemText 
                            primary="OWNER DISPLAY NAME" 
                            secondary={`${ownerDetails.name || '-'}${ownerDetails.isMocked ? ' (Mocked)' : ''}`} 
                            primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                            secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                          />
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <ListItemText 
                            primary="OWNER EMAIL ADDRESS" 
                            secondary={`${ownerDetails.email || '-'}${ownerDetails.isMocked ? ' (Mocked)' : ''}`} 
                            primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                            secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                          />
                        </ListItem>
                      </>
                    ) : null}

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        ACCOUNT ROLE
                      </Typography>
                      <Chip
                        label={selectedAccount.role}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          ...getRoleChipColor(selectedAccount.role)
                        }}
                      />
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

              {/* Subscription Details Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Billing & Subscription Contract
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        SUBSCRIPTION TYPE
                      </Typography>
                      <Chip
                        label={selectedAccount.subscriptionType}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          ...getTypeChipColor(selectedAccount.subscriptionType)
                        }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        CONTRACT STATUS
                      </Typography>
                      <Chip
                        label={selectedAccount.subscriptionStatus.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          ...getStatusChipColor(selectedAccount.subscriptionStatus)
                        }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        START DATE
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(selectedAccount.subscriptionStartDate)}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        EXPIRATION DATE
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: selectedAccount.subscriptionStatus === 'ACTIVE' ? 'inherit' : theme.palette.error.main }}>
                        {formatDate(selectedAccount.subscriptionEndDate)}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        REFERRAL PROMO CODE
                      </Typography>
                      <Chip
                        label={selectedAccount.referralCode || "None"}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '6px',
                          borderColor: theme.palette.outlineVariant
                        }}
                      />
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

              {/* Profile Quota Progress Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 3, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Profile Quota Utilization
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress
                        variant="determinate"
                        value={selectedAccount.maxProfilesAllowed >= 9999 ? 0 : Math.min(100, Math.round((selectedAccount.currentProfileCount / selectedAccount.maxProfilesAllowed) * 100))}
                        size={80}
                        thickness={5}
                        sx={{
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.surfaceContainerHighest,
                          borderRadius: '50%'
                        }}
                      />
                      <Box sx={{
                        top: 0, left: 0, bottom: 0, right: 0,
                        position: 'absolute',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {selectedAccount.maxProfilesAllowed >= 9999 ? "∞" : `${Math.round((selectedAccount.currentProfileCount / selectedAccount.maxProfilesAllowed) * 100)}%`}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        Profiles Created vs Plan Limit
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700 }}>
                            CREATED
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            {selectedAccount.currentProfileCount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ borderLeft: `1px solid ${theme.palette.divider}`, pl: 3 }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700 }}>
                            PLAN LIMIT
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            {selectedAccount.maxProfilesAllowed >= 9999 ? "Unlimited" : selectedAccount.maxProfilesAllowed.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Auditing Fingerprint Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Audit & Identity Footprint
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        CREATED BY
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {selectedAccount.createdBy}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        CREATED AT
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {formatDate(selectedAccount.createdAt)}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        UPDATED BY
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {selectedAccount.updatedBy || '-'}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        LAST UPDATED AT
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {formatDate(selectedAccount.updatedAt)}
                      </Typography>
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

            </Box>

            {/* Read-Only Footer Context */}
            <Box sx={{ 
              p: 3, 
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.surfaceContainerLow,
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <AdminIcon style={{ fontSize: '16px' }} />
                Administrative Console • Read-Only View Mode
              </Typography>
            </Box>

          </Box>
        )}
      </Drawer>

    </Box>
  );
};

export default TenantManagementTab;
